/**
 * Creative Performance's two labels (spec 2026-10-15-native-creative-performance §4.3), kept beside the Insights
 * engine whose thresholds they share, so Insights can adopt them later:
 * - Fatigue: the second half's CTR is more than FATIGUE_CTR_DROP below the first half's;
 * - Top: the lowest Cost per Result in its pool (workspace × channel × objective × currency).
 *
 * Pure. Relative imports only, as the rest of this folder: api/_lib/runDigests.ts bundles this folder's engine
 * without the "@/" alias.
 *
 * @module services/insights/creativeLabels
 */
import {
  BENCHMARK_MIN_IMPRESSIONS,
  FATIGUE_CTR_DROP,
  MIN_CLICKS_30D,
  MIN_CONVERSIONS_30D,
  MIN_IMPRESSIONS_30D,
  MIN_POOL_SIZE,
} from "./insightConstants";

/** The share of the first half's CTR the second half must keep, in whole percent: 75 for a 25% drop. */
export const KEEP_PCT = Math.round((1 - FATIGUE_CTR_DROP) * 100);

/** One creative's two halves (spec §4.1); NULL = no row in that half, read as 0 (spec §4.3). */
export interface CreativeHalves {
  impressionsFirst: number | null;
  clicksFirst: number | null;
  impressionsSecond: number | null;
  clicksSecond: number | null;
}

/**
 * The CTR drop from the first half to the second (0.3 = 30%) when the creative is Fatigue, else null (R7). Both halves
 * need BENCHMARK_MIN_IMPRESSIONS impressions and the first half a click. The test runs on whole counts,
 * 100 × c₂ × i₁ < KEEP_PCT × c₁ × i₂, so an exact 25% drop is never a label (as float CTRs, 0.075 < 0.1 × 0.75 would
 * hold and label it). The products stay exact below 2^53.
 */
export const fatigueDropOf = (h: CreativeHalves): number | null => {
  const i1 = h.impressionsFirst ?? 0;
  const c1 = h.clicksFirst ?? 0;
  const i2 = h.impressionsSecond ?? 0;
  const c2 = h.clicksSecond ?? 0;
  if (i1 < BENCHMARK_MIN_IMPRESSIONS || i2 < BENCHMARK_MIN_IMPRESSIONS || c1 <= 0) return null;
  if (100 * c2 * i1 >= KEEP_PCT * c1 * i2) return null;
  return 1 - (c2 * i1) / (c1 * i2);
};

/** What Top needs of one creative (R8). The caller works out lowSample (D1's isLowSample) and the Cost per Result. */
export interface TopCandidate {
  adId: string;
  workspaceId: string;
  platform: string;
  objective: string;
  currency: string;
  /** The period's spend: the first tie-break, NULL after every number. */
  spend: number | null;
  lowSample: boolean;
  costPerResult: number | null;
  /** The Result window's sums, for Insights' volume floors, never scaled to the window's length. */
  resultImpressions: number | null;
  resultClicks: number | null;
  resultConversions: number | null;
}

type Ranked = TopCandidate & { costPerResult: number };

/** Insights' volume floor for the objective, over the Result window; any other objective never qualifies. */
const meetsFloor = (c: TopCandidate): boolean => {
  if (c.objective === "conversion") return (c.resultConversions ?? 0) >= MIN_CONVERSIONS_30D;
  if (c.objective === "consideration") return (c.resultClicks ?? 0) >= MIN_CLICKS_30D;
  if (c.objective === "awareness") return (c.resultImpressions ?? 0) >= MIN_IMPRESSIONS_30D;
  return false;
};

/** Not n<500, a Cost per Result above 0 (Insights ranks only ads with spend > 0), and the objective's volume floor. */
export const isTopEligible = (c: TopCandidate): c is Ranked =>
  !c.lowSample && c.costPerResult !== null && c.costPerResult > 0 && meetsFloor(c);

/** The higher spend first; NULL after every number. */
const bySpendDesc = (a: number | null, b: number | null): number => {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return b - a;
};

/** The lowest Cost per Result; ties go to the higher spend, then the lower adId. */
const byTopOrder = (a: Ranked, b: Ranked): number =>
  a.costPerResult - b.costPerResult || bySpendDesc(a.spend, b.spend) || (a.adId < b.adId ? -1 : Number(a.adId > b.adId));

const poolKeyOf = (c: TopCandidate): string => [c.workspaceId, c.platform, c.objective, c.currency].join("|");

/**
 * The adIds that carry Top (R8): one per pool of at least MIN_POOL_SIZE eligible creatives. Pools never mix
 * workspaces, so a creative carries the same label in the all-workspaces view as in its own workspace.
 */
export const topWinnersOf = (candidates: readonly TopCandidate[]): ReadonlySet<string> => {
  // Built and read only here: the accepted local mutation (Global Constraints).
  const pools = new Map<string, Ranked[]>();
  candidates.filter(isTopEligible).forEach((c) => pools.set(poolKeyOf(c), [...(pools.get(poolKeyOf(c)) ?? []), c]));
  return new Set(
    [...pools.values()]
      .filter((pool) => pool.length >= MIN_POOL_SIZE)
      .map((pool) => [...pool].sort(byTopOrder)[0].adId),
  );
};
